import type { LocationNode, Zone } from '../api/types'

/** Zone kinds (topology.ZoneKind), in picker order. */
export const ZONE_KINDS = [
  { value: 'alarm_area', label: 'Alarm area' },
  { value: 'anti_passback', label: 'Anti-passback' },
  { value: 'rollcall', label: 'Rollcall' },
  { value: 'generic', label: 'Generic' },
] as const

/** Chip tone per zone kind (Locations card, Zones page). */
export const ZONE_KIND_TONE: Record<string, 'gold' | 'info' | 'warn' | 'muted'> = {
  alarm_area: 'gold',
  anti_passback: 'info',
  rollcall: 'warn',
  generic: 'muted',
}

/** uids from the root down to `uid` (inclusive); [] when not in the tree. */
export function ancestorChain(nodes: LocationNode[], uid: string): string[] {
  for (const node of nodes) {
    if (node.unique_id === uid) return [uid]
    const below = ancestorChain(node.children, uid)
    if (below.length > 0) return [node.unique_id, ...below]
  }
  return []
}

export interface CoveringZone {
  zone: Zone
  /** null = attached to this location itself; otherwise the nearest
   *  ancestor location the zone is attached to (it covers everything
   *  beneath it, e.g. a zone on an address covers its floors). */
  via: string | null
}

/** Zones covering `uid`: attached directly, or to any ancestor. Direct
 *  ones first, then inherited ones nearest-ancestor first. */
export function coveringZones(zones: Zone[], tree: LocationNode[], uid: string): CoveringZone[] {
  const chain = ancestorChain(tree, uid)
  const ancestors = chain.slice(0, -1).reverse() // nearest first
  const direct: CoveringZone[] = []
  const inherited: { zone: Zone; via: string; depth: number }[] = []
  for (const zone of zones) {
    if (zone.locations.includes(uid)) {
      direct.push({ zone, via: null })
      continue
    }
    const depth = ancestors.findIndex((a) => zone.locations.includes(a))
    if (depth >= 0) inherited.push({ zone, via: ancestors[depth], depth })
  }
  inherited.sort((a, b) => a.depth - b.depth)
  return [...direct, ...inherited.map(({ zone, via }) => ({ zone, via }))]
}

/** Compact location list for a zone row: "Sandalettgatan 28 +2". */
export function zoneLocationSummary(zone: Zone, names: Map<string, string>): string {
  if (zone.locations.length === 0) return 'No locations'
  const first = names.get(zone.locations[0]) ?? zone.locations[0]
  return zone.locations.length > 1 ? `${first} +${zone.locations.length - 1}` : first
}
